// src/utils/usePrayerTimes.js
import { useState, useEffect, useRef, useCallback } from "react";
import fetchPrayerTimesFromAPI from "./fetchPrayerTimesFromAPI";

const CACHE_KEY = (zone) => `prayer_cache_${zone}`;

const readCache = (zone) => {
    try {
        const raw = localStorage.getItem(CACHE_KEY(zone));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeCache = (zone, payload) => {
    try {
        localStorage.setItem(CACHE_KEY(zone), JSON.stringify(payload));
    } catch { }
};

const localDateKey = () => {
    // YYYY-MM-DD ikut local timezone
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

// cuba normalize dateKey dari API (kalau API bagi "YYYY-MM-DD")
const toDateKeyFromApi = (maybeDate) => {
    const s = String(maybeDate || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    return null;
};

// ===== hook =====
const usePrayerTimes = (zoneCode) => {
    const [prayerTimes, setPrayerTimes] = useState(null);
    const [currentPrayer, setCurrentPrayer] = useState("");
    const [nextPrayer, setNextPrayer] = useState("");
    const [countdown, setCountdown] = useState("--:--:--");
    const [countdownZero, setCountdownZero] = useState(false);
    const [dayDate, setDayDate] = useState("");
    const [hijriDate, setHijriDate] = useState("");
    const [tomorrowHijriDate, setTomorrowHijriDate] = useState("");

    const [nextDayPrayerTimes, setNextDayPrayerTimes] = useState(null);
    const [isNextPrayerCalculated, setIsNextPrayerCalculated] = useState(false);

    const countdownTimerRef = useRef(null);

    const parseTimeToSeconds = useCallback((time) => {
        if (!time) return 0;
        const [hours, minutes, seconds] = String(time).split(":").map(Number);
        const totalSeconds = (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
        return totalSeconds;
    }, []);

    const formatCountdown = useCallback((remainingSeconds) => {
        const hours = String(Math.floor(remainingSeconds / 3600)).padStart(2, "0");
        const minutes = String(Math.floor((remainingSeconds % 3600) / 60)).padStart(2, "0");
        const seconds = String(remainingSeconds % 60).padStart(2, "0");
        return `${hours}:${minutes}:${seconds}`;
    }, []);

    // ✅ fetcher (boleh dipanggil dari effect lain)
    const fetchData = useCallback(async () => {
        try {
            // hari ini
            const data = await fetchPrayerTimesFromAPI(zoneCode);

            // esok
            const nextDayData = await fetchPrayerTimesFromAPI(zoneCode, "tomorrow");

            setPrayerTimes(data.todayTimes);
            setDayDate(data.todayTimes?.date || "");
            setHijriDate(data.todayTimes?.hijri || "");
            setTomorrowHijriDate(data?.tomorrowHijri || "");
            setNextDayPrayerTimes(nextDayData?.todayTimes || null);

            // ✅ dateKey ikut API kalau ada, supaya tak jadi "hari ni" walau data lama
            const keyFromApi = toDateKeyFromApi(data.todayTimes?.date);
            const key = keyFromApi || localDateKey();

            writeCache(zoneCode, {
                dateKey: key,
                todayTimes: data.todayTimes,
                nextDayTimes: nextDayData?.todayTimes || null,
                hijriDate: data.todayTimes?.hijri || "",
                tomorrowHijriDate: data?.tomorrowHijri || "",
                currentPrayer: "",
                nextPrayer: "",
                countdown: "--:--:--",
                savedAt: Date.now(),
            });

            setIsNextPrayerCalculated(false);
        } catch (error) {
            console.error("Error fetching prayer times:", error);
        }
    }, [zoneCode]);

    const calculateNextPrayer = useCallback(
        (times, nextDayTimes) => {
            const setupCountdown = (nextPrayerTimeInSeconds) => {
                if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

                countdownTimerRef.current = setInterval(() => {
                    const now = new Date();
                    const nowInSeconds =
                        now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

                    const remainingSeconds = nextPrayerTimeInSeconds - nowInSeconds;

                    if (remainingSeconds <= 0) {
                        clearInterval(countdownTimerRef.current);
                        countdownTimerRef.current = null;

                        setCountdownZero(true);
                        setTimeout(() => setCountdownZero(false), 60000);

                        // recalc untuk dapat next selepas masuk waktu
                        calculateNextPrayer(times, nextDayTimes);
                    } else {
                        setCountdown(formatCountdown(remainingSeconds));
                    }
                }, 1000);
            };

            if (!times) return;

            const now = new Date();
            const nowInSeconds =
                now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

            const prayers = ["imsak", "fajr", "syuruk", "dhuha", "dhuhr", "asr", "maghrib", "isha"];

            const timesInSeconds = prayers.map((prayer) => ({
                name: prayer,
                time: parseTimeToSeconds(times[prayer]),
            }));

            // next day's imsak untuk handle lepas midnight
            if (nextDayTimes) {
                timesInSeconds.push({
                    name: "imsak",
                    time: parseTimeToSeconds(nextDayTimes.imsak) + 86400,
                });
            }

            const currentIndex = timesInSeconds.findIndex((pt) => pt.time > nowInSeconds);
            const nextIndex = currentIndex === -1 ? 0 : currentIndex;
            const previousIndex = (currentIndex === -1 ? timesInSeconds.length : currentIndex) - 1;

            const cur = (timesInSeconds[previousIndex] || {}).name || "";
            const nxt = (timesInSeconds[nextIndex] || {}).name || "";

            setCurrentPrayer(cur);
            setNextPrayer(nxt);

            // hijri selepas maghrib
            const maghribSeconds = parseTimeToSeconds(times.maghrib);
            const hijriToUse = maghribSeconds < nowInSeconds ? tomorrowHijriDate : times.hijri;

            setHijriDate(hijriToUse);

            const nextSec = timesInSeconds[nextIndex].time;
            const remainingAtStart = nextSec - nowInSeconds;
            const countdownNow = remainingAtStart > 0 ? formatCountdown(remainingAtStart) : "00:00:00";

            setCountdown(countdownNow);
            setupCountdown(nextSec);

            // ✅ UPDATE cache display state sahaja (JANGAN ubah dateKey/todayTimes!)
            const prev = readCache(zoneCode) || {};
            writeCache(zoneCode, {
                ...prev,
                currentPrayer: cur,
                nextPrayer: nxt,
                countdown: countdownNow,
                hijriDate: hijriToUse,
                tomorrowHijriDate: tomorrowHijriDate,
                savedAt: Date.now(),
            });
        },
        [parseTimeToSeconds, formatCountdown, tomorrowHijriDate, zoneCode]
    );

    // ===== init/hydrate + fetch =====
    useEffect(() => {
        // 0) clear interval bila zone berubah
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }

        const cached = readCache(zoneCode);
        const todayKey = localDateKey();

        // 1) hydrate dulu supaya UI tak kosong
        if (cached?.todayTimes) {
            setPrayerTimes(cached.todayTimes);
            setDayDate(cached.todayTimes?.date || "");
            setHijriDate(cached.hijriDate || "");
            setTomorrowHijriDate(cached.tomorrowHijriDate || "");
            setNextDayPrayerTimes(cached.nextDayTimes || null);
            setCurrentPrayer(cached.currentPrayer || "");
            setNextPrayer(cached.nextPrayer || "");
            setCountdown(cached.countdown || "--:--:--");
            setCountdownZero(false);
            setIsNextPrayerCalculated(false);
        }

        // 2) decide fetch: kalau dateKey mismatch → fetch
        // IMPORTANT: dateKey sekarang TAK boleh dipercayai jika sebelum ni bug,
        // tapi selepas patch ni ia akan stabil. Still: jika mismatch → fetch.
        if (!(cached?.dateKey === todayKey && cached?.todayTimes)) {
            fetchData();
        }

        return () => {
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
                countdownTimerRef.current = null;
            }
        };
    }, [zoneCode, fetchData]);

    // ===== refresh bila app resume (PWA) + TTL =====
    useEffect(() => {
        const TTL_MS = 2 * 60 * 60 * 1000; // 2 jam

        const maybeRefresh = () => {
            const cached = readCache(zoneCode);
            const todayKey = localDateKey();

            // hari tukar / cache tak ada → fetch
            if (!cached || !cached.todayTimes || cached.dateKey !== todayKey) {
                fetchData();
                return;
            }

            // cache terlalu lama → fetch
            const age = Date.now() - (cached.savedAt || 0);
            if (age > TTL_MS) {
                fetchData();
            }
        };

        const onFocus = () => maybeRefresh();
        const onVis = () => {
            if (document.visibilityState === "visible") maybeRefresh();
        };

        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVis);

        // run sekali bila mount
        maybeRefresh();

        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [zoneCode, fetchData]);

    // ===== run calc bila data ready =====
    useEffect(() => {
        if (prayerTimes && !isNextPrayerCalculated) {
            calculateNextPrayer(prayerTimes, nextDayPrayerTimes);
            setIsNextPrayerCalculated(true);
        }
    }, [prayerTimes, nextDayPrayerTimes, isNextPrayerCalculated, calculateNextPrayer]);

    return {
        prayerTimes,
        nextDayPrayerTimes,
        currentPrayer,
        nextPrayer,
        countdown,
        countdownZero,
        dayDate,
        hijriDate,
    };
};

export default usePrayerTimes;
